import React from "react";
import { useShopMode } from "../context/ShopModeContext";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Sparkles, ShoppingBag, ShieldCheck, ArrowRight, CheckCircle2, X } from "lucide-react";

export const ShopChoiceModal = () => {
  const { showChoiceModal, selectModeAndCloseModal, closeChoiceModal } = useShopMode();

  return (
    <Dialog open={showChoiceModal} onOpenChange={(open) => { if (!open) closeChoiceModal(); }}>
      <DialogContent 
        className="w-[95vw] sm:w-[90vw] max-w-4xl max-h-[96vh] md:max-h-[90vh] flex flex-col p-0 overflow-hidden bg-white border-0 shadow-2xl rounded-2xl sm:rounded-3xl [&>button:last-child]:hidden"
        data-testid="shop-choice-modal"
      >
        {/* Top Dark Banner */}
        <div className="relative bg-[#17130e] text-white pt-3 pb-2.5 px-3 sm:pt-4 sm:pb-3.5 sm:px-6 text-center border-b border-stone-800 shrink-0">
          {/* Top Right Close Button */}
          <button
            type="button"
            onClick={closeChoiceModal}
            className="absolute top-2.5 right-2.5 sm:top-3.5 sm:right-3.5 w-7 h-7 rounded-full flex items-center justify-center text-stone-400 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Badge: DIRECT FROM MANUFACTURER */}
          <div className="inline-flex items-center gap-1 sm:gap-1.5 px-2.5 py-0.5 sm:px-3 sm:py-0.5 rounded-full bg-[#261f13] border border-[#b8860b]/40 text-[#dfb85e] text-[9px] sm:text-[11px] font-bold tracking-wider uppercase mb-1 sm:mb-2 shadow-xs">
            <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#dfb85e]" />
            <span>DIRECT FROM MANUFACTURER</span>
          </div>

          {/* Main Title */}
          <DialogTitle className="font-serif-display text-base sm:text-xl md:text-2xl font-bold tracking-tight text-white uppercase mb-0.5 leading-tight">
            HOW WOULD YOU LIKE TO SHOP?
          </DialogTitle>

          {/* Subtitle */}
          <p className="text-stone-300 text-[10px] sm:text-xs max-w-lg mx-auto leading-normal sm:leading-relaxed">
            Welcome to <strong className="text-[#dfb85e] font-bold">Zaveri Trunk</strong>. Please choose your desired experience.
          </p>
        </div>

        {/* Main Content Area */}
        <div className="overflow-y-auto flex-1 p-3 sm:p-5 md:p-6 space-y-2.5 sm:space-y-4">
          <div className="grid grid-cols-2 gap-2 sm:gap-4 md:gap-5 items-stretch">
            {/* RETAIL CARD (LEFT) */}
            <div
              data-testid="shop-choice-retail"
              onClick={() => selectModeAndCloseModal("retail")}
              className="rounded-xl sm:rounded-2xl p-2.5 sm:p-4 md:p-5 border-2 border-[#162a45] bg-white flex flex-col justify-between hover:shadow-xl hover:border-blue-600 transition-all cursor-pointer group text-left"
            >
              <div>
                {/* Header: Icon & Badge */}
                <div className="flex items-center justify-between gap-1 sm:gap-2">
                  <div className="w-7 h-7 xs:w-8 xs:h-8 sm:w-11 sm:h-11 rounded-lg sm:rounded-xl bg-[#eef4fc] border border-blue-200/70 flex items-center justify-center text-[#162a45] group-hover:scale-105 transition-transform shadow-xs shrink-0">
                    <ShoppingBag className="w-3.5 h-3.5 xs:w-4 xs:h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" strokeWidth={1.75} />
                  </div>
                  <span className="bg-[#dbeafe] text-[#1e40af] text-[7.5px] xs:text-[8.5px] sm:text-xs font-bold uppercase tracking-wider px-1.5 py-0.5 sm:px-2.5 sm:py-0.5 rounded-full border border-blue-200 shadow-xs whitespace-nowrap shrink-0">
                    B2C RETAIL
                  </span>
                </div>

                {/* Title & Subtitle */}
                <h3 className="font-serif-display text-sm sm:text-lg md:text-xl font-bold text-stone-900 tracking-wide mt-1.5 sm:mt-2.5 mb-0.5 group-hover:text-[#162a45] transition-colors">
                  RETAIL
                </h3>
                <p className="text-[9.5px] sm:text-xs text-stone-500 font-medium mb-2 sm:mb-3">
                  Ready Complete Looks
                </p>

                {/* Features List */}
                <ul className="space-y-1 sm:space-y-1.5 mb-2 sm:mb-3 text-[9.5px] sm:text-xs text-stone-700 font-medium">
                  <li className="flex items-start gap-1.5 sm:gap-2 leading-tight">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#162a45] shrink-0 mt-0.5" strokeWidth={2} />
                    <span>Curated complete looks</span>
                  </li>
                  <li className="flex items-start gap-1.5 sm:gap-2 leading-tight">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#162a45] shrink-0 mt-0.5" strokeWidth={2} />
                    <span>Transparent retail prices</span>
                  </li>
                  <li className="flex items-start gap-1.5 sm:gap-2 leading-tight">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#162a45] shrink-0 mt-0.5" strokeWidth={2} />
                    <span>Fast 1-step checkout</span>
                  </li>
                  <li className="flex items-start gap-1.5 sm:gap-2 leading-tight">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#162a45] shrink-0 mt-0.5" strokeWidth={2} />
                    <span>UPI, Cards & COD</span>
                  </li>
                </ul>
              </div>

              {/* Bottom CTA Link */}
              <div className="flex items-center justify-between pt-1.5 sm:pt-2.5 border-t border-stone-100 text-[10.5px] sm:text-xs md:text-sm font-bold text-[#162a45] group-hover:translate-x-0.5 transition-transform mt-1 sm:mt-2">
                <span>Shop Retail</span>
                <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </div>
            </div>

            {/* WHOLESALE CARD (RIGHT) */}
            <div
              data-testid="shop-choice-wholesale"
              onClick={() => selectModeAndCloseModal("wholesale")}
              className="rounded-xl sm:rounded-2xl p-2.5 sm:p-4 md:p-5 border-2 border-stone-800 bg-white flex flex-col justify-between hover:shadow-xl hover:border-[#976008] transition-all cursor-pointer group text-left"
            >
              <div>
                {/* Header: Icon & Badge */}
                <div className="flex items-center justify-between gap-1 sm:gap-2">
                  <div className="w-7 h-7 xs:w-8 xs:h-8 sm:w-11 sm:h-11 rounded-lg sm:rounded-xl bg-[#fef6e7] border border-amber-200/70 flex items-center justify-center group-hover:scale-105 transition-transform shadow-xs shrink-0">
                    <svg className="w-3.5 h-3.5 xs:w-4 xs:h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-[#976008]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="5" y="3" width="14" height="13" rx="2" />
                      <line x1="8" y1="7" x2="16" y2="7" />
                      <line x1="8" y1="11" x2="16" y2="11" />
                      <line x1="8" y1="14" x2="12" y2="14" />
                      <path d="M7 21h10" />
                      <path d="M12 16v5" />
                    </svg>
                  </div>
                  <span className="bg-[#fef3d6] text-[#976008] text-[7.5px] xs:text-[8.5px] sm:text-xs font-bold uppercase tracking-wider px-1.5 py-0.5 sm:px-2.5 sm:py-0.5 rounded-full border border-amber-200 shadow-xs whitespace-nowrap shrink-0">
                    B2B WHOLESALE
                  </span>
                </div>

                {/* Title & Subtitle */}
                <h3 className="font-serif-display text-sm sm:text-lg md:text-xl font-bold text-stone-900 tracking-wide mt-1.5 sm:mt-2.5 mb-0.5 group-hover:text-[#976008] transition-colors">
                  WHOLESALE
                </h3>
                <p className="text-[9.5px] sm:text-xs text-stone-500 font-medium mb-2 sm:mb-3">
                  For Retailers & Resellers
                </p>

                {/* Features List */}
                <ul className="space-y-1 sm:space-y-1.5 mb-2 sm:mb-3 text-[9.5px] sm:text-xs text-stone-700 font-medium">
                  <li className="flex items-start gap-1.5 sm:gap-2 leading-tight">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#976008] shrink-0 mt-0.5" strokeWidth={2} />
                    <span>Direct factory pricing</span>
                  </li>
                  <li className="flex items-start gap-1.5 sm:gap-2 leading-tight">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#976008] shrink-0 mt-0.5" strokeWidth={2} />
                    <span>WhatsApp bulk enquiries</span>
                  </li>
                  <li className="flex items-start gap-1.5 sm:gap-2 leading-tight">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#976008] shrink-0 mt-0.5" strokeWidth={2} />
                    <span>Custom requirement uploads</span>
                  </li>
                  <li className="flex items-start gap-1.5 sm:gap-2 leading-tight">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#976008] shrink-0 mt-0.5" strokeWidth={2} />
                    <span>Volume tier discounts</span>
                  </li>
                </ul>
              </div>

              {/* Bottom CTA Link */}
              <div className="flex items-center justify-between pt-1.5 sm:pt-2.5 border-t border-stone-100 text-[10.5px] sm:text-xs md:text-sm font-bold text-[#976008] group-hover:translate-x-0.5 transition-transform mt-1 sm:mt-2">
                <span>Enter Wholesale</span>
                <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </div>
            </div>
          </div>

          {/* Bottom Trust Guarantee */}
          <div className="flex items-center justify-center gap-1.5 sm:gap-2 pt-0.5 sm:pt-1 text-[10px] sm:text-xs text-stone-500 font-medium text-center">
            <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-600 shrink-0" />
            <span>Direct manufacturer jewellery marketplace with hallmark quality.</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
